import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, ShieldCheck, Truck, Pill, Stethoscope, FlaskConical, Umbrella, Bell } from "lucide-react";
import Layout from "../components/layout/Layout";
import CategoryTile from "../components/CategoryTile";
import ProductCard from "../components/ProductCard";
import { SkeletonGrid } from "../components/SkeletonCard";
import TodayDosesWidget from "../components/reminder/TodayDosesWidget";
import UpcomingThisWeek from "../components/dashboard/UpcomingThisWeek";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

const Home = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [cats, setCats] = useState([]);
    const [conditions, setConditions] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const [{ data: c }, { data: cond }, { data: p }] = await Promise.all([
                    api.get("/categories"),
                    api.get("/conditions"),
                    api.get("/products", { params: { page_size: 8, rx_only: false, sort: "price_desc" } }),
                ]);
                if (!mounted) return;
                setCats(c);
                setConditions((cond.items || []).slice(0, 12));
                setProducts(p.items || []);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, []);

    return (
        <Layout>
            {/* Hero */}
            <section className="relative overflow-hidden bg-white" data-testid="home-hero">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 -z-10 bg-brand-radial"
                />
                <div className="mx-auto max-w-7xl px-6 pb-20 pt-16 sm:pt-24 lg:px-12 lg:pt-28">
                    <div className="grid items-center gap-12 lg:grid-cols-2">
                        <div className="animate-fade-up">
                            <div className="chip mb-6 border-brand-200 bg-brand-50/80 text-brand-700">
                                <Sparkles className="h-3.5 w-3.5" /> {t("AI-powered healthcare commerce")}
                            </div>
                            <h1 className="text-4xl font-semibold leading-[1.05] tracking-tight text-zinc-900 sm:text-5xl lg:text-6xl">
                                {t("Pharmacy, reimagined")}<br />{t("for modern India.")}
                            </h1>
                            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg">
                                {t("Genuine medicines, supplements and devices — curated, verified and delivered. A premium experience for everyday wellness.")}
                            </p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <Link to="/products" className="btn-brand" data-testid="hero-shop-btn">
                                    {t("Browse all products")}
                                </Link>
                                <Link
                                    to="/signup"
                                    className="inline-flex items-center justify-center gap-2 rounded-full border border-zinc-200 bg-white px-6 py-3 text-sm font-medium text-zinc-900 transition-all hover:border-brand-300 hover:text-brand-700"
                                    data-testid="hero-signup-btn"
                                >
                                    {t("Create account")}
                                </Link>
                            </div>

                            <ul className="mt-8 grid grid-cols-3 gap-4 text-xs text-zinc-600">
                                <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-700" /> {t("Verified")}</li>
                                <li className="flex items-center gap-2"><Truck className="h-4 w-4 text-brand-700" /> {t("Fast ship")}</li>
                                <li className="flex items-center gap-2"><Pill className="h-4 w-4 text-brand-700" /> {t("10+ cats")}</li>
                            </ul>
                        </div>

                        <div className="relative animate-fade-up">
                            <div className="absolute -inset-8 -z-10 rounded-[3rem] bg-brand-gradient opacity-20 blur-3xl" />
                            <div className="glass-card overflow-hidden rounded-[2rem] p-2 shadow-brand">
                                <img
                                    src="https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=1200&q=80"
                                    alt="Premium pharmacy"
                                    className="h-[420px] w-full rounded-[1.6rem] object-cover"
                                />
                            </div>
                            <div className="glass absolute -bottom-6 -left-6 rounded-2xl px-5 py-4 shadow-brand">
                                <p className="label-uppercase">Today</p>
                                <p className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">{products.length ? "160+ products" : "—"} • {cats.length || "10"} categories</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* v3A — Prominent 5-service tiles row (top of page after hero) */}
            <section className="mx-auto max-w-7xl px-6 pt-8 lg:px-12" data-testid="home-services-hero">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                    <Link
                        to="/products"
                        className="group relative overflow-hidden rounded-2xl border border-zinc-100 bg-gradient-to-br from-brand-50 to-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:border-brand-200 dark:from-brand-950/30 dark:to-zinc-900 dark:border-white/10"
                        data-testid="home-svc-medicines"
                    >
                        <Pill className="h-7 w-7 text-brand-700" />
                        <h3 className="mt-3 text-base font-semibold text-zinc-900 dark:text-zinc-50">{t("Buy Medicines")}</h3>
                        <p className="mt-1 text-xs text-zinc-500">{t("160+ SKUs, prescription verified")}</p>
                        <span className="mt-3 inline-flex text-[11px] font-semibold uppercase tracking-wider text-brand-700">{t("Shop")} →</span>
                    </Link>
                    <Link
                        to="/consultations"
                        className="group relative overflow-hidden rounded-2xl border border-zinc-100 bg-gradient-to-br from-violet-50 to-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:border-violet-200 dark:from-violet-950/30 dark:to-zinc-900 dark:border-white/10"
                        data-testid="home-svc-consult"
                    >
                        <Stethoscope className="h-7 w-7 text-violet-700" />
                        <h3 className="mt-3 text-base font-semibold text-zinc-900 dark:text-zinc-50">{t("Consult a Doctor")}</h3>
                        <p className="mt-1 text-xs text-zinc-500">{t("Verified specialists, chat now")}</p>
                        <span className="mt-3 inline-flex text-[11px] font-semibold uppercase tracking-wider text-violet-700">{t("Book")} →</span>
                    </Link>
                    <Link
                        to="/lab-tests"
                        className="group relative overflow-hidden rounded-2xl border border-zinc-100 bg-gradient-to-br from-emerald-50 to-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:border-emerald-200 dark:from-emerald-950/30 dark:to-zinc-900 dark:border-white/10"
                        data-testid="home-svc-labs"
                    >
                        <FlaskConical className="h-7 w-7 text-emerald-700" />
                        <h3 className="mt-3 text-base font-semibold text-zinc-900 dark:text-zinc-50">{t("Book Lab Test")}</h3>
                        <p className="mt-1 text-xs text-zinc-500">{t("Home pickup, 24-hr reports")}</p>
                        <span className="mt-3 inline-flex text-[11px] font-semibold uppercase tracking-wider text-emerald-700">{t("Book")} →</span>
                    </Link>
                    <Link
                        to="/insurance"
                        className="group relative overflow-hidden rounded-2xl border border-zinc-100 bg-gradient-to-br from-amber-50 to-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:border-amber-200 dark:from-amber-950/30 dark:to-zinc-900 dark:border-white/10"
                        data-testid="home-svc-insurance"
                    >
                        <Umbrella className="h-7 w-7 text-amber-700" />
                        <h3 className="mt-3 text-base font-semibold text-zinc-900 dark:text-zinc-50">{t("Order Insurance")}</h3>
                        <p className="mt-1 text-xs text-zinc-500">{t("Curated, advisor-backed plans")}</p>
                        <span className="mt-3 inline-flex text-[11px] font-semibold uppercase tracking-wider text-amber-700">{t("Explore")} →</span>
                    </Link>
                    <Link
                        to="/reminders"
                        className="group relative overflow-hidden rounded-2xl border border-brand-200 bg-gradient-to-br from-purple-100 to-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg dark:from-purple-950/30 dark:to-zinc-900 dark:border-purple-700/30"
                        data-testid="home-svc-reminders"
                    >
                        <span className="absolute right-2 top-2 rounded-full bg-brand-700 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wider text-white">{t("New")}</span>
                        <Bell className="h-7 w-7 text-brand-700" />
                        <h3 className="mt-3 text-base font-semibold text-zinc-900 dark:text-zinc-50">{t("PILLS Reminder")}</h3>
                        <p className="mt-1 text-xs text-zinc-500">{t("Smart dose & refill alerts")}</p>
                        <span className="mt-3 inline-flex text-[11px] font-semibold uppercase tracking-wider text-brand-700">{t("Set up")} →</span>
                    </Link>
                </div>
            </section>

            {/* v3E.1 — Today's doses widget for signed-in users */}
            {user && (
                <section className="mx-auto max-w-7xl px-6 pt-8 lg:px-12" data-testid="home-today-doses">
                    <TodayDosesWidget compact />
                </section>
            )}

            {/* v3E.3 — Upcoming this week */}
            {user && (
                <section className="mx-auto max-w-7xl px-6 pt-6 lg:px-12" data-testid="home-upcoming-week">
                    <UpcomingThisWeek />
                </section>
            )}

            {/* Categories */}
            <section className="mx-auto max-w-7xl px-6 py-16 lg:px-12" data-testid="home-categories-section">
                <div className="mb-8 flex items-end justify-between">
                    <div>
                        <p className="label-uppercase">{t("Shop by category")}</p>
                        <h2 className="mt-2 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">{t("Find what you need")}</h2>
                    </div>
                    <Link to="/products" className="hidden text-sm font-medium text-brand-700 hover:underline md:inline">{t("View all")} →</Link>
                </div>
                {loading ? (
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                        {Array.from({ length: 10 }).map((_, i) => (
                            <div key={i} className="aspect-[4/3] rounded-2xl skeleton" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                        {cats.map((c) => <CategoryTile key={c.id} category={c} />)}
                    </div>
                )}
            </section>

            {/* Shop by condition */}
            <section className="mx-auto w-full max-w-7xl overflow-x-hidden px-6 pb-12 lg:px-12" data-testid="home-conditions-section">
                <div className="mb-5 flex items-end justify-between gap-3">
                    <div>
                        <p className="label-uppercase text-zinc-500">Shop by</p>
                        <h2 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 sm:text-3xl dark:text-zinc-50" style={{ letterSpacing: "-0.01em" }}>Condition</h2>
                    </div>
                    <Link to="/conditions" className="text-sm font-medium text-brand-700 hover:underline" data-testid="home-conditions-all-link">View all →</Link>
                </div>
                <div className="flex w-full max-w-full snap-x snap-mandatory gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" data-testid="home-conditions-scroll">
                    {(loading ? Array.from({ length: 8 }) : conditions).map((c, i) => c ? (
                        <Link
                            key={c.slug}
                            to={`/conditions/${c.slug}`}
                            className="group relative w-32 max-w-[40vw] shrink-0 snap-start overflow-hidden rounded-2xl border border-zinc-100 bg-white p-4 transition-shadow hover:shadow-md sm:w-44 dark:bg-white/[0.03] dark:border-white/10"
                            data-testid={`home-condition-${c.slug}`}
                        >
                            <span className={`absolute -right-4 -top-4 h-16 w-16 rounded-full bg-gradient-to-br ${c.hero_color} opacity-25 transition-opacity group-hover:opacity-40`} aria-hidden />
                            <span className="relative text-2xl" aria-hidden>{c.icon_emoji}</span>
                            <p className="relative mt-2 text-sm font-medium text-zinc-900 line-clamp-1 dark:text-zinc-50">{c.name}</p>
                            <p className="relative mt-0.5 text-[10px] text-zinc-500">{c.product_count} product{c.product_count === 1 ? "" : "s"}</p>
                        </Link>
                    ) : <div key={i} className="h-28 w-32 max-w-[40vw] shrink-0 rounded-2xl skeleton sm:w-44" />)}
                </div>
            </section>

            {/* Featured products */}
            <section className="mx-auto max-w-7xl px-6 pb-20 lg:px-12" data-testid="home-featured-section">
                <div className="mb-8 flex items-end justify-between">
                    <div>
                        <p className="label-uppercase">{t("Trending")}</p>
                        <h2 className="mt-2 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">{t("Featured products")}</h2>
                    </div>
                    <Link to="/products" className="text-sm font-medium text-brand-700 hover:underline">{t("All products")} →</Link>
                </div>
                {loading ? (
                    <SkeletonGrid count={8} />
                ) : (
                    <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
                        {products.map((p) => <ProductCard key={p.id} product={p} />)}
                    </div>
                )}
            </section>
        </Layout>
    );
};

export default Home;
